import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";

const AuthCtx = createContext(null);

function computeDemoInfo(clinic) {
  if (!clinic) return null;
  const isDemo = clinic.is_demo || false;
  if (!isDemo) return { is_demo: false, demo_expirado: false, dias_restantes: null };
  // dias_restantes and demo_expirado may come from backend or we compute locally
  if (clinic.dias_restantes !== undefined) {
    return {
      is_demo: true,
      demo_expires_at: clinic.demo_expires_at || null,
      dias_restantes: clinic.dias_restantes,
      demo_expirado: clinic.demo_expirado || false,
    };
  }
  // Fallback: compute from demo_expires_at
  const expires = clinic.demo_expires_at ? new Date(clinic.demo_expires_at) : null;
  if (!expires) return { is_demo: true, demo_expirado: false, dias_restantes: null };
  const now = new Date();
  const msLeft = expires - now;
  const dias = Math.max(0, Math.floor(msLeft / 86400000));
  return { is_demo: true, demo_expires_at: clinic.demo_expires_at, dias_restantes: dias, demo_expirado: msLeft <= 0 };
}

// COL.10 -- las capacidades del usuario y su ALCANCE llegan del backend, en `/auth/me`.
// 🔴 LAS 9 CAPACIDADES NO SE ESCRIBEN AQUI. Su catalogo (codigo, nombre, descripcion,
//    orden) viaja en la respuesta de `GET /api/colaboradores` y se pinta desde ahi. Una
//    segunda lista en JavaScript es la forma exacta de DT.235 / DT.247 / DT.248 -- cuatro
//    apariciones ya del mismo defecto: el dato correcto escrito dos veces, que se
//    desincroniza justo cuando nadie esta mirando.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [activeClinic, setActiveClinic] = useState(null);

  useEffect(() => {
    const bloquear = ({ detail }) => setUser((actual) => {
      if (!actual || actual.is_demo_session || (detail.clinicId && detail.clinicId !== actual.active_clinic_id)) return actual;
      if (actual.plan_vigente?.vigente === false && actual.plan_vigente.motivo === detail.motivo) return actual;
      return { ...actual, plan_vigente: { vigente: false, motivo: detail.motivo } };
    });
    window.addEventListener("surit:plan-no-vigente", bloquear);
    return () => window.removeEventListener("surit:plan-no-vigente", bloquear);
  }, []);

  const demoInfo = useMemo(() => computeDemoInfo(activeClinic), [activeClinic]);

  // 🔴 `capacidades` es `null` mientras NO SE SABE y un arreglo cuando ya se sabe, aunque
  //    este vacio. La diferencia importa: `null` = "todavia no lo se", `[]` = "no tiene
  //    ninguna". Colapsar los dos hace que un fallo de carga se vea igual que una cuenta
  //    sin permisos -- el mismo cero ambiguo que ya mordio en B3-a y que el repo repite
  //    con `.catch(() => setX([]))` en varios sitios.
  const capacidades = user && Array.isArray(user.capacidades) ? user.capacidades : null;
  const puede = useMemo(
    () => (cap) => !!capacidades && capacidades.includes(cap),
    [capacidades]
  );
  // El alcance viene RESUELTO del backend (`alcance_total`); aqui no se deduce de una
  // lista vacia, que es como se lee al reves (COL.8: ausencia de filas = TODA la clinica).
  const alcance = useMemo(
    () => ({
      total: user ? user.alcance_total !== false : true,
      doctores: (user && user.alcance_doctores) || [],
    }),
    [user]
  );

  const loadClinics = async () => {
    try {
      const { data } = await api.get("/clinics/mine");
      setClinics(data.items);
      const ac = data.items.find((c) => c.id === data.active_clinic_id) || data.items[0] || null;
      setActiveClinic(ac);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("surit_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then(async (r) => {
        localStorage.setItem("surit_user", JSON.stringify(r.data));
        setUser(r.data);
        await loadClinics();
      })
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("surit_token", data.access_token);
    // 🔴 EL USUARIO SE LEE DE `/auth/me`, NO DE LA RESPUESTA DEL LOGIN, y no es un rodeo:
    //    el login devuelve la fila de `users` mas sus clinicas, SIN `capacidades` ni
    //    `alcance` -- los calcula `get_current_user`. Usando `data.user` directamente, el
    //    primer render tras entrar tendria `capacidades` en `undefined` y el menu se
    //    pintaria VACIO hasta la primera recarga. Es exactamente el bug de
    //    `is_demo_session` de PUENTE-4/5, y se evita teniendo UNA sola forma del usuario.
    //    `/auth/me` esta en `_RUTAS_CON_PASSWORD_TEMPORAL`, asi que responde tambien
    //    cuando la contrasena vigente es temporal -- que es justo cuando hace falta.
    const { data: yo } = await api.get("/auth/me");
    localStorage.setItem("surit_user", JSON.stringify(yo));
    setUser(yo);
    await loadClinics();
    return yo;
  };

  const demoLogin = async () => {
    const { data } = await api.post("/auth/demo-login");
    localStorage.setItem("surit_token", data.access_token);
    const { data: yo } = await api.get("/auth/me");   // mismo motivo que en login()
    localStorage.setItem("surit_user", JSON.stringify(yo));
    setUser(yo);
    await loadClinics();
    return yo;
  };

  const logout = () => {
    localStorage.removeItem("surit_token");
    localStorage.removeItem("surit_user");
    setUser(false);
    setClinics([]);
    setActiveClinic(null);
  };

  const refrescarUsuario = async () => {
    const { data: yo } = await api.get("/auth/me");
    setUser(actual => {
      if (!actual || actual.active_clinic_id !== yo.active_clinic_id) return actual;
      localStorage.setItem("surit_user", JSON.stringify(yo));
      return yo;
    });
  };

  const switchClinic = async (clinic_id) => {
    const { data } = await api.post("/clinics/switch", { clinic_id });
    setActiveClinic(data.active_clinic);
    // Las capacidades y el alcance son POR CLINICA: al cambiar de clinica hay que
    // releerlos, no arrastrar los de la anterior. Sin esto, alguien con `colaboradores`
    // en una clinica veria esa entrada del menu estando en otra donde no la tiene -- y
    // acabaria justo en el 403 que el menu existe para no provocar.
    const { data: yo } = await api.get("/auth/me");
    localStorage.setItem("surit_user", JSON.stringify(yo));
    setUser(yo);
    // soft refresh: trigger a 'clinicChanged' event so pages can reload data
    window.dispatchEvent(new CustomEvent("surit:clinic-changed", { detail: data.active_clinic }));
    return data.active_clinic;
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, logout, demoLogin, clinics, activeClinic, switchClinic, refrescarUsuario, demoInfo, capacidades, puede, alcance }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
